import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { inflate } from 'zlib';

import { environment } from '../../environments/environment';
import { AuthenticationService } from './authentication.service';
import { CredentialService } from './credential.service';

import { OA, RFID, SHEETS } from '../../ngrx/category-types';

import { Batch, Die, DiePressure, ProductSubAssembly } from '../models';

import {
  allBatchFields,
  allDieFields,
  allProductSubAssemblyFields
} from '../../ngrx/graphql';

import { CLOSED, statusTypes } from '../../ngrx/status-types';

const errorCodes = [400, 401, 403, 404];

/** Ink priority for PTO printing: Gold < Silver < Clear < CMYK < other */
const PTO_INK_ORDER: { [key: string]: number } = {
  gold : 0,
  silver: 1,
  clear: 2,
  cmyk : 3
};

@Injectable()
export class BatchService {
  constructor(
    private authService      : AuthenticationService,
    private credentialService: CredentialService,
    private router           : Router,
    private store            : Store<any>
  ) {}

  private invokeBatches(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.batchFunction,
        Payload     : JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
            observer.error(err.stack);
          } else {
            const output = typeof data.Payload === 'string'
                ? JSON.parse(data.Payload)
                :       data.Payload;
            if(output.gzip){
              const options = {
                windowBits : 15
              };
              inflate(new Buffer(output.body,'base64'), options, (err, data) => {
                if (err) {                    
                  observer.error(err.stack);
                } else {
                  const unzippedOutput = Object.assign({}, output, {
                    body: data.toString()
                  });
                  observer.next(unzippedOutput);
                }
              });
            } else {
              observer.next(output);
            }
          }
        }
      );
    });
  }

  private invokeBatchMutation(payload: any): void {
    this.credentialService.lambda.invoke({
      FunctionName: environment.batchFunction,
      Payload     : JSON.stringify(payload)
      }, (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
            // this.authService.logout();
          }
        }
      }
    );
  }

  formatBatch(batch: any): Batch {
    const jobs = batch.jobs
      ? batch.jobs.map(batchJob => Object.assign({}, batchJob, {
        jobsList: batchJob.jobsList.map(listJob => Object.assign({}, listJob, {
          productSubAssembly: (listJob.productSubAssembly && listJob.productSubAssembly.sku) || null
        }))
      }))
      :  null;

    // Note: this scrap array is holding not the feet of scrap, but the key to the scrap record in the scrap table....
    let scrap = batch.scrap ? batch.scrap.map(scrap => scrap.id) : null;

    // empty arrays are still truthy
    if (scrap) {
      scrap = scrap.filter(id => !!id); // remove all undefined entries
      console.log(scrap.length);
      if (scrap.length < 1) {
        scrap = null;
      }
    }

      

    const formattedBatch = Object.assign({}, batch, { jobs, scrap });

    if (batch.hasOwnProperty('colorReprint')) delete formattedBatch.colorReprint;
    if (batch.hasOwnProperty('ttl')) delete formattedBatch.ttl;
    if (batch.hasOwnProperty('daysUntilDue')) delete formattedBatch.daysUntilDue;
    if (batch.hasOwnProperty('daysUntilDueMsg')) delete formattedBatch.daysUntilDueMsg;
    if (batch.hasOwnProperty('diffNBD')) delete formattedBatch.diffNBD;
    if (batch.hasOwnProperty('reprint')) delete formattedBatch.reprint;
    if (batch.hasOwnProperty('status')) delete formattedBatch.status;
    if (batch.hasOwnProperty('updatedAt')) delete formattedBatch.updatedAt;
    if (batch.hasOwnProperty('numberOfSheets')) delete formattedBatch.numberOfSheets;
    if (batch.hasOwnProperty('cutting')) delete formattedBatch.cutting;
    if (batch.hasOwnProperty('lines')) delete formattedBatch.lines;
    if (batch.hasOwnProperty('coating')) delete formattedBatch.coating;
    if (batch.hasOwnProperty('createdAt')) delete formattedBatch.createdAt;
    if (batch.hasOwnProperty('updatedAt')) delete formattedBatch.updatedAt;
    
    return formattedBatch;
  }

  formatSubAssembly(subAssembly: any): ProductSubAssembly {
    const formattedSubAssembly = Object.assign({}, subAssembly);

    if (subAssembly.hasOwnProperty('updatedAt')) delete formattedSubAssembly.updatedAt;
    if (subAssembly.hasOwnProperty('createdAt')) delete formattedSubAssembly.createdAt;

    return formattedSubAssembly;
  }

  /** Extract specialty ink from PTO compKey (e.g. Gold, Silver, Clear, CMYK) */
  getPTOInkFromCompKey(compKey: string): string {
    if (!compKey) return '';
    const ck = String(compKey);
    
    const ptoIdx = ck.toUpperCase().indexOf('_PTO_');
    if (ptoIdx === -1) return '';

    const after = ck.substring(ptoIdx + 5);
    const tokens = after.split('_').filter(Boolean);
    const filtered = tokens.filter(t => {
      const v = t.toLowerCase();
      if (v === 'versafire' || v === 'printing' || v === 'cutting' || v === 'cardstock') return false;
      if (/^\d+$/.test(v)) return false;
      if (/^\d{2}-\d{2}-\d{4}$/.test(v)) return false;
      return true;
    });
    const ink = filtered.length ? filtered[filtered.length - 1] : '';
    
    return ink.replace(/_/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  /** Sort rank for PTO ink: Gold=0, Silver=1, Clear=2, CMYK=3, other=4 */
  getPTOInkSortRank(compKey: string): number {
    const ink = this.getPTOInkFromCompKey(compKey || '');
    if (PTO_INK_ORDER.hasOwnProperty(ink)) return PTO_INK_ORDER[ink];
    return 4;
  }

  /** Sort PTO printing order: due date (earliest first / soonest due on top), then ink (Gold→Silver→Clear→CMYK), then totalQty (desc). */
  comparePTOPrintingBatches(a: any, b: any): number {
    const dueA = (a && a.dueDate) && !isNaN(Date.parse(a.dueDate)) ? new Date(a.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
    const dueB = (b && b.dueDate) && !isNaN(Date.parse(b.dueDate)) ? new Date(b.dueDate).getTime() : Number.MAX_SAFE_INTEGER;
    if (dueA !== dueB) return dueA - dueB; // earliest due date first
    const rankA = this.getPTOInkSortRank((a && a.compKey) || '');
    const rankB = this.getPTOInkSortRank((b && b.compKey) || '');
    if (rankA !== rankB) return rankA - rankB;
    const qa = (a && a.totalQty) || 0;
    const qb = (b && b.totalQty) || 0;
    return qb - qa;
  }

  /** Sort PTO printing batches: due date → ink → totalQty. */
  sortPTOPrintingBatches(batches: any[]): any[] {
    return (batches || []).slice().sort((a, b) => this.comparePTOPrintingBatches(a, b));
  }

  removeBatchedJob(batch: Batch, jobID: string): Batch {
    const updatedBatch = Object.assign({}, this.formatBatch(batch));

    const jobs = batch.jobs
      .map(batchJob => Object.assign({}, batchJob, {
        jobsList: batchJob.jobsList.filter(listJob => listJob.jobId !== jobID)
      }))
      .filter(batchJob => batchJob.jobsList.length);
    
    updatedBatch.numberOfJobs = batch.numberOfJobs - 1;
    updatedBatch.jobs         = jobs;
    
    if (!updatedBatch.numberOfJobs) {
      updatedBatch.batchStatus = CLOSED;
    }

    console.log('removing job from batch...');
    console.log(updatedBatch);

    return updatedBatch;
  }

  createBatch(batch: any): void {
    const formattedBatch = this.formatBatch(batch);

    const query = `mutation($batch: BatchCreateInput!) {
      createBatch(batch: $batch) {
        ${allBatchFields}
      }
    }`;

    const variables = { batch: formattedBatch };

    this.invokeBatchMutation({ query, variables });
  }

  createBatchResult(batch: any): Observable<any> {
    const formattedBatch = this.formatBatch(batch);

    const query = `mutation($batch: BatchCreateInput!) {
      createBatch(batch: $batch) {
        ${allBatchFields}
      }
    }`;

    const variables = { batch: formattedBatch };

    return this.invokeBatches({ query, variables });
  }

  deleteBatches(ids: string[]): void {
    const query = `mutation($ids: [ID]) {
      deleteBatches(ids: $ids) {
        id
      }
    }`;

    const variables = { ids };

    this.invokeBatchMutation({ query, variables });
  }

  getActiveOABatches(): Observable<any> {
    const query = `{
      activeBatches(category: "${OA}") {
        ${allBatchFields}
      }
    }`;

    return this.invokeBatches({ query });
  }

  getAll(): Observable<any> {
    const query = `{
      activeBatches(category: "${SHEETS}") {
        ${allBatchFields}
      }

      productSubAssemblies(param: "all") {
        ${allProductSubAssemblyFields}
      }
    }`;

    return this.invokeBatches({ query });
  }

  getAllOA(): Observable<any> {
    const query = `{
      activeBatches(category: "${OA}") {
        ${allBatchFields}
      }

      productSubAssemblies(param: "all") {
        ${allProductSubAssemblyFields}
      }
    }`;

    return this.invokeBatches({ query });
  }

  getAllRFID(): Observable<any> {
    const query = `{
      activeBatches(category: "${RFID}") {
        ${allBatchFields}
      }

      productSubAssemblies(param: "all") {
        ${allProductSubAssemblyFields}
      }
    }`;

    return this.invokeBatches({ query });
  }

  getBatch(id: string): Observable<any> {
    const query = `{
      batch(id: "${id}") {
        ${allBatchFields}
      }
    }`;

    return this.invokeBatches({ query });
  }

  getBatchDetailsView(): Observable<any> {
    return this.store.select('batches', 'batchDetailsView');
  }

  getBatchList(): Observable<Batch[]> {
    return this.store.select('batches', 'batchList');
  }

  getBatchListView(): Observable<any> {
    return this.store.select('batches', 'batchListView');
  }

  getBatches(): Observable<any> {
    const query = `{
      batches(param: "all") {
        ${allBatchFields}
      }
    }`;

    return this.invokeBatches({ query });
  }

  getBatchesByHorizonId(id: string): Observable<any> {
    const query = `{
      batchesByHorizonId(horizon_id: "${id}") {
        ${allBatchFields}
      }
    }`;

    return this.invokeBatches({ query });
  }

  getBatchesByID(ids: string[]): Observable<any> {
    let formattedIDs = '';
    ids.forEach(id => formattedIDs += `"${id}", `);
    
    const query = `{
      batchesByID(ids: [${formattedIDs.trim()}]) {
        ${allBatchFields}
      }
    }`;

    return this.invokeBatches({ query });
  }

  getDieList(): Observable<Die[]> {
    return this.store.select('dies', 'dieList');
  }

  getDieListView(): Observable<any> {
    return this.store.select('dies', 'dieListView');
  }

  getProductSubAssemblyList(): Observable<ProductSubAssembly[]> {
    return this.store.select('products', 'productSubAssemblies');
  }

  getProductSubAssembly(sku: string): Observable<any> {
    const query = `{
     productSubAssembly(sku: "${sku}") {
       ${allProductSubAssemblyFields}
     }
    }`;

    return this.invokeBatches({ query });
  }

  getProductSubAssemblies(): Observable<any> {
    const query = `{
      productSubAssemblies(param: "all") {
        ${allProductSubAssemblyFields}
      }
    }`;

    return this.invokeBatches({ query });
  }

  getProductSubAssembliesOA(): Observable<any> {
    const query = `{
      productSubAssembliesOA(param: "all") {
        ${allProductSubAssemblyFields}
      }
    }`;

    return this.invokeBatches({ query });
  }

  getSortedOABatchList(): Observable<any> {
    return this.store.select('batches', 'sortedOABatches');
  }

  getPtoBatchList(): Observable<any> {
    return this.store.select('batches', 'ptoBatches');
  }

  getPTOPreprintBatchList(): Observable<any> {
    return this.store.select('batches', 'ptoPreprintBatches');
  }

  getPTOPrintingBatchList(): Observable<any> {
    return this.store.select('batches', 'ptoPrintingBatches');
  }

  getPTOSealedPrintingBatchList(): Observable<any> {
    return this.store.select('batches', 'ptoSealedPrintingBatches');
  }

  updateBatch(batch: Batch): void {
    console.log(`updating batch: ${JSON.stringify(batch, null, 2)}`);
    if (batch) {
      const formattedBatch = this.formatBatch(batch);
      console.log(formattedBatch)
      const variables = { batch: formattedBatch };
      const query     = `mutation($batch: BatchInput!) {
        updateBatch(batch: $batch) {
          ${allBatchFields}
        }
      }`;

      this.invokeBatchMutation({ query, variables });
    }
  }

  updateBatchResult(batch: Batch): Observable<any> {
    console.log(`updating batch: ${JSON.stringify(batch, null, 2)}`);
    
    const formattedBatch = this.formatBatch(batch);

    const variables = { batch: formattedBatch };
    const query     = `mutation($batch: BatchInput!) {
      updateBatch(batch: $batch) {
        ${allBatchFields}
      }
    }`;

    return this.invokeBatches({ query, variables });
  }

  updateBatches(batches: Batch[]): void {
    console.log(`updating batches`);
    console.log(batches);
    if (batches && batches.length) {
      const formattedBatches = batches.map(batch => this.formatBatch(batch));

      const variables = { batches: formattedBatches };
      const query     = `mutation($batches: [BatchInput]) {
        updateBatches(batches: $batches) {
          ${allBatchFields}
        }
      }`;

      this.invokeBatchMutation({ query, variables });
    }
  }

  updateBatchStatus(id: string, batchStatus: string, note?: string, user?: string): void {
    if(!id || !batchStatus) {
      console.log(`updateBatchStatus: missing id or batchStatus: ${id}, ${batchStatus}`);
      return;
    }

    const query = `mutation($id: ID!, $batchStatus: String!, $note: String, $user: String) {
      updateBatchStatus(id: $id, batchStatus: $batchStatus, note: $note, user: $user) {
        ${allBatchFields}
      }
    }`;
    const variables = { id, batchStatus, note, user };
    this.invokeBatchMutation({ query, variables });
  }

  updateDie(die: Die): void {
    console.log(`updating die`, die);
    if(typeof die.usage_stats != 'string') {
      die.usage_stats = JSON.stringify(die.usage_stats);
    }
    console.log("die after usage Stats: ",die);


    if (die) {
      const variables = { die };
      const query = `mutation($die: DieInput!) {
        updateDie(die: $die) {
          ${allDieFields}
        }
      }`;

      this.invokeBatchMutation({ query, variables });
    }
  }

  appendDieScanHistoryAsync(dieId: string, user: string, timestamp: string, orderId?: string, jobId?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const query = `mutation($dieId: ID!, $user: String!, $timestamp: String!, $orderId: String, $jobId: String) {
        appendDieScanHistory(dieId: $dieId, user: $user, timestamp: $timestamp, orderId: $orderId, jobId: $jobId) {
          ${allDieFields}
        }
      }`;
      const variables = { dieId, user, timestamp, orderId: orderId || null, jobId: jobId || null };
      this.credentialService.lambda.invoke({
        FunctionName: environment.batchFunction,
        Payload     : JSON.stringify({ query, variables })
      }, (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
          }
          reject(err);
          return;
        }
        const output = typeof data.Payload === 'string'
          ? JSON.parse(data.Payload)
          : data.Payload;
        if (output.gzip) {
          const options = { windowBits: 15 };
          inflate(new Buffer(output.body, 'base64'), options, (inflateErr, inflated) => {
            if (inflateErr) {
              reject(inflateErr);
            } else {
              resolve(Object.assign({}, output, { body: inflated.toString() }));
            }
          });
        } else {
          resolve(output);
        }
      });
    });
  }

  updateAllDiePressureValues(die: Die, pressures: DiePressure[]): void {
    if (!die || !pressures || !pressures.length) {
      console.log(`updateAllDiePressureValues: missing parameter(s)`);
      return;
    }

    const updatedDie = die.die_pressures.map(diePressure => {
      
    });
  }

  updateDiePressureValue(die: Die, pressure: number, batch: Batch, orderID: string, jobID: string): void {
    if (!die || (pressure !== 0 && !pressure) || !batch || !orderID || !jobID) {
      console.log(`updateDiePressureValue: missing parameter(s)`);
      return;
    }

    const updatedDie = Object.assign({}, die, {
      die_pressures: die.die_pressures.map(diePressure => {
        if (diePressure.subAssemblyID !== batch.subAssembly) {
          return diePressure;
        }

        return Object.assign({}, diePressure, {
          currentPressure: pressure,
          diePressureRecords: [
            {
              oldPressure: diePressure.currentPressure,
              newPressure: pressure,
              user: this.authService.getCurrentUser(),
              order: orderID.startsWith('OA')
                ? orderID.substring(2)
                : orderID,
              job: jobID,
              timestamp: new Date().getTime()
            },
            ...diePressure.diePressureRecords
          ]
        });
      })
    });

    this.updateDie(updatedDie);
  }

  updateSubAssembly(subAssembly: ProductSubAssembly): void {
    console.log(`updating sub assembly: ${JSON.stringify(subAssembly, null, 2)}`);
    if (subAssembly) {
      const formattedSubAssembly = this.formatSubAssembly(subAssembly);

      const variables = { subAssembly: formattedSubAssembly };
      const query     = `mutation($subAssembly: ProductSubAssemblyInput!) {
        updateProductSubAssembly(productSubAssembly: $subAssembly) {
          ${allProductSubAssemblyFields}
        }
      }`;

      this.invokeBatchMutation({ query, variables });
    }
  }

  getBatchWrapper(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.getBatch(id)
        .take(1)
        .subscribe((value: any) => {
          const { statusCode, errorMessage } = value;
          if (statusCode === 400 || errorMessage) {
            reject(JSON.parse(value.body).errors[0].message);
          } else {
            // This will still resolve if query not found
            resolve(JSON.parse(value.body).data.batch);
          }
        },
          (error: Error) => {
            reject(error);
          }
        )
    })
  }

  getHorizonLoad(id: string): Observable<any> {
    const query = `query($id: ID!) {
      horizonLoad(id: $id) {
        id
        totalPendingSheets
        type
        isAvailable
        createdAt
        updatedAt
      }
    }`;

    const variables = { id };
    const payload = { query, variables };

    return this.invokeBatches(payload);
  }

  updateHorizonLoadAtomic(id: string, totalPendingSheetsChange: number): void {
    const query = `mutation($input: HorizonLoadAtomicInput!) {
      updateHorizonLoadAtomic(input: $input) {
        id
        totalPendingSheets
        type
        isAvailable
      }
    }`;

    const variables = { input: { id, totalPendingSheetsChange } };
    const payload = { query, variables };

    this.invokeBatches(payload)
      .take(1)
      .subscribe(
        (response: any) => {
          console.log(`Updated horizon load for ${id} by ${totalPendingSheetsChange} sheets`);
        },
        (error: Error) => {
          console.error(`Error updating horizon load: ${error}`);
        }
      );
  }

  getPtoBins(): Observable<any> {
    const query = `query {
      ptoBins {
        id
        category
        rackNumber
        batchCompKey
        batchId
        lastUpdated
      }
    }`;

    const payload = { query };
    return this.invokeBatches(payload);
  }

  initializePtoBins(): Observable<any> {
    const mutation = `mutation {
      initializePtoBins
    }`;
    const payload = { query: mutation };
    return this.invokeBatches(payload);
  }

  clearPtoBin(rackNumber: number): Observable<any> {
    const mutation = `mutation {
      clearPtoBin(rackNumber: ${rackNumber})
    }`;
    const payload = { query: mutation };
    return this.invokeBatches(payload);
  }
}


// WEBPACK FOOTER //
// /Users/itu/Desktop/impreso_updated_sheets/impresocloud/www/client/src/app/services/batch.service.ts